/**
 * HistorySidebar — Wrangle-pattern: search + session list with "..." context
 * menu (Rename / Delete). Rename is done inline.
 */

import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { formatSessionListTitle } from '../agentMessageWindow';
import { deleteSession, isGenericSessionTitle, listRecentSessions, renameSession } from '../agentSession';
import type { AgentSession } from '../types';
import type { Collection } from './AgentJobsPanel';

export interface HistorySidebarProps {
  activeSessionId: string | null;
  onSelectSession: (session: AgentSession) => void;
  onNewSession: () => void;
  refreshKey?: number;
  onClose?: () => void;
  onSessionRenamed?: (sessionId: string, title: string | null) => void;
  onSessionDeleted?: (sessionId: string) => void;
  onBackToWorkspace?: () => void;
  /** Collections to show below chat history */
  collections?: Collection[];
  activeCollectionId?: string | null;
  onSelectCollection?: (id: string | null) => void;
  /** Compact panel opened from AgentFloatingNav — hides duplicate New task CTA */
  floating?: boolean;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function groupByDate(sessions: AgentSession[]): { label: string; items: AgentSession[] }[] {
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();

  const groups: Record<string, AgentSession[]> = {
    Today: [],
    Yesterday: [],
    'This week': [],
    Earlier: [],
  };

  for (const s of sessions) {
    const d = new Date(s.updatedAt);
    const ds = d.toDateString();
    if (ds === today) groups['Today'].push(s);
    else if (ds === yesterdayStr) groups['Yesterday'].push(s);
    else if (now.getTime() - d.getTime() < 7 * 24 * 60 * 60 * 1000) groups['This week'].push(s);
    else groups['Earlier'].push(s);
  }

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

// Deterministic color from string
const AVATAR_COLORS = [
  '#6C47FF', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899',
];
function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(title: string | null | undefined): string {
  const t = title?.trim() || 'U';
  const parts = t.split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : t.slice(0, 2).toUpperCase();
}

// ── Component ─────────────────────────────────────────────────────────────────

const HistorySidebar: React.FC<HistorySidebarProps> = ({
  activeSessionId,
  onSelectSession,
  onNewSession,
  refreshKey = 0,
  onClose,
  onSessionRenamed,
  onSessionDeleted,
  onBackToWorkspace,
  collections = [],
  activeCollectionId = null,
  onSelectCollection,
  floating = false,
}) => {
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  // rename
  const [editingId, setEditingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameBusyId, setRenameBusyId] = useState<string | null>(null);

  // delete
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // "..." dropdown open state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // collapsed date groups
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // ── data load ──
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listRecentSessions(50);
      setSessions(rows);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload, refreshKey]);

  // close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── actions ──
  const handleDelete = useCallback(async (id: string) => {
    setOpenMenuId(null);
    if (!confirm('Delete this conversation?')) return;
    setPendingDeleteId(id);
    try {
      await deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (editingId === id) setEditingId(null);
      onSessionDeleted?.(id);
    } finally {
      setPendingDeleteId(null);
    }
  }, [editingId, onSessionDeleted]);

  const startRename = useCallback((s: AgentSession) => {
    setOpenMenuId(null);
    setEditingId(s.id);
    setRenameDraft(s.title?.trim() || '');
  }, []);

  const commitRename = useCallback(async () => {
    if (!editingId) return;
    const id = editingId;
    const next = renameDraft.trim().slice(0, 200);
    setRenameBusyId(id);
    try {
      await renameSession(id, next);
      const normalized: string | null = next.length ? next : null;
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, title: normalized } : s)),
      );
      onSessionRenamed?.(id, normalized);
      setEditingId(null);
    } catch (err) {
      console.warn('[ai-agent] renameSession failed:', err);
    } finally {
      setRenameBusyId(null);
    }
  }, [editingId, renameDraft, onSessionRenamed]);

  const cancelRename = useCallback(() => setEditingId(null), []);

  const toggleGroup = useCallback((label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  }, []);

  // ── filtered + grouped ──
  // Hide generic sessions that aren't active — they are usually empty/abandoned.
  const visibleSessions = sessions.filter(
    (s) => !isGenericSessionTitle(s.title) || s.id === activeSessionId,
  );

  const filtered = query.trim()
    ? visibleSessions.filter((s) =>
        (isGenericSessionTitle(s.title) ? 'New task' : s.title ?? '').toLowerCase().includes(query.trim().toLowerCase()),
      )
    : visibleSessions;

  const groups = groupByDate(filtered);

  // ── render ──
  return (
    <aside
      className={`flex flex-col h-full w-full select-none ${floating ? '' : 'ds-card-flush'}`}
      style={{
        borderRight: floating ? undefined : '1px solid var(--ds-border)',
        borderRadius: floating ? undefined : 0,
      }}
      aria-label="Task sessions"
    >
      {/* ── Header ── */}
      <div className="px-3 pt-3 pb-2 flex flex-col gap-2">
        {floating && onClose && (
          <div className="flex items-center justify-between gap-2 pb-0.5">
            <span className="t-card-title" style={{ color: 'var(--ds-text-strong)' }}>History</span>
            <button
              type="button"
              onClick={onClose}
              className="ds-btn ds-btn-ghost w-8 h-8 p-0 shrink-0"
              aria-label="Close history"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {onBackToWorkspace && (
          <button
            type="button"
            onClick={onBackToWorkspace}
            className="ds-btn ds-btn-ghost w-full justify-start"
            title="Back to main workspace"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to workspace
          </button>
        )}

        {!floating && (
          <button
            type="button"
            onClick={() => { onNewSession(); onClose?.(); }}
            className="ds-btn ds-btn-secondary w-full"
          >
            <Plus className="w-4 h-4" style={{ color: 'var(--ds-brand)' }} />
            New task
            <span className="ml-auto t-tag opacity-40 font-mono">⌘K</span>
          </button>
        )}

        {/* Search box */}
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
          style={{
            background:  'var(--ds-bg-tinted)',
            border:      '1px solid var(--ds-border)',
          }}
        >
          <Search className="w-3.5 h-3.5 shrink-0 t-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks…"
            className="flex-1 min-w-0 bg-transparent outline-none t-meta"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="shrink-0 opacity-50 hover:opacity-100">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* ── Session list ── */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--ds-border) transparent' }}>
        {loading && sessions.length === 0 ? (
          <div className="flex items-center justify-center py-8 t-meta">
            <LoadingSpinner size="xs" showText={false} /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-2 py-6 t-meta text-center">
            {query ? 'No chats match your search.' : 'No conversations yet. Start a new chat.'}
          </div>
        ) : (
          <div className="flex flex-col gap-0" ref={menuRef}>
            {groups.map(({ label, items }) => {
              const collapsed = collapsedGroups.has(label);
              return (
                <div key={label} className="mb-1">
                  {/* Group header */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(label)}
                    className="w-full flex items-center gap-1 px-2 py-1 rounded t-label transition-opacity hover:opacity-80"
                  >
                    {collapsed
                      ? <ChevronRight className="w-3 h-3" />
                      : <ChevronDown className="w-3 h-3" />
                    }
                    {label}
                  </button>

                  {!collapsed && (
                    <ul className="flex flex-col gap-0.5">
                      {items.map((s) => {
                        const isActive = s.id === activeSessionId;
                        const editing = editingId === s.id;
                        const menuOpen = openMenuId === s.id;
                        const color = avatarColor(s.id);
                        const label2 = isGenericSessionTitle(s.title)
                          ? 'New task'
                          : formatSessionListTitle(s.title, s.updatedAt);

                        return (
                          <li key={s.id} className="relative">
                            <div
                              className="group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors"
                              style={{
                                background: isActive
                                  ? 'var(--ds-brand-soft)'
                                  : menuOpen ? 'var(--ds-bg-tinted)' : 'transparent',
                              }}
                              onClick={() => {
                                if (editing) return;
                                onSelectSession(s);
                                onClose?.();
                              }}
                              onMouseEnter={(e) => {
                                if (!isActive && !menuOpen) e.currentTarget.style.background = 'var(--ds-bg-tinted)';
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive && !menuOpen) e.currentTarget.style.background = 'transparent';
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (editing) return;
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectSession(s); onClose?.(); }
                              }}
                            >
                              {/* Avatar circle */}
                              {!editing && (
                                <div
                                  className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                                  style={{ background: color }}
                                >
                                  {initials(label2)}
                                </div>
                              )}

                              {/* Title / inline rename input */}
                              <div className="flex-1 min-w-0">
                                {editing ? (
                                  <input
                                    autoFocus
                                    value={renameDraft}
                                    disabled={renameBusyId === s.id}
                                    onChange={(e) => setRenameDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); void commitRename(); }
                                      if (e.key === 'Escape') cancelRename();
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full rounded-md px-2 py-0.5 t-meta outline-none border"
                                    style={{
                                      background:  'var(--ds-bg-card)',
                                      borderColor: 'var(--ds-brand)',
                                      color:       'var(--ds-text-strong)',
                                    }}
                                  />
                                ) : (
                                  <span
                                    className="block t-meta truncate font-medium"
                                    style={{ color: isActive ? 'var(--ds-brand)' : 'var(--ds-text-strong)' }}
                                  >
                                    {label2}
                                  </span>
                                )}
                              </div>

                              {/* Confirm/cancel row when editing */}
                              {editing ? (
                                <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={() => void commitRename()}
                                    disabled={!!renameBusyId}
                                    className="w-6 h-6 flex items-center justify-center rounded"
                                  style={{ background: undefined }}
                                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--ds-brand-soft)'; }}
                                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
                                    aria-label="Save"
                                  >
                                    {renameBusyId === s.id
                                      ? <LoadingSpinner size="xs" showText={false} />
                                      : <Check className="w-3 h-3" style={{ color: 'var(--ds-brand)' }} />
                                    }
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelRename}
                                  className="w-6 h-6 flex items-center justify-center rounded hover:opacity-70"
                                  aria-label="Cancel"
                                >
                                  <X className="w-3 h-3 t-muted" />
                                  </button>
                                </div>
                              ) : (
                                /* "..." three-dot button — visible on hover or when menu open */
                                <button
                                  type="button"
                                  aria-label="More options"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(menuOpen ? null : s.id);
                                  }}
                                  className="shrink-0 w-6 h-6 flex items-center justify-center rounded transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
                                  style={{
                                    opacity: menuOpen ? 1 : undefined,
                                    color: 'var(--ds-text-body)',
                                  }}
                                >
                                  <MoreHorizontal className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>

                            {/* Dropdown menu */}
                            {menuOpen && !editing && (
                              <div
                                className="absolute right-2 z-50 mt-0.5 rounded-xl shadow-lg py-1 min-w-[130px]"
                                style={{
                                  top:        '100%',
                                  background: 'var(--ds-bg-card)',
                                  border:     '1px solid var(--ds-border)',
                                  boxShadow:  'var(--ds-shadow-pop)',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() => startRename(s)}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 t-body transition-colors rounded-lg"
                                  style={{ background: undefined }}
                                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--ds-brand-soft)'; }}
                                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
                                >
                                  <Pencil className="w-3.5 h-3.5 t-muted" />
                                  Rename
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleDelete(s.id)}
                                  disabled={!!pendingDeleteId}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 t-body t-danger transition-colors rounded-lg"
                                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--ds-danger-bg)'; }}
                                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
                                >
                                  {pendingDeleteId === s.id
                                    ? <LoadingSpinner size="xs" showText={false} />
                                    : <Trash2 className="w-3.5 h-3.5" />
                                  }
                                  Delete
                                </button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

    </aside>
  );
};

export default HistorySidebar;
